import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiResponse } from "@/api/rest/core/types";
import {
  HausingRoom,
  HausingRoomMapping,
  HausingSettings as HausingSettingsData,
  UpdateHausingSettingsRequest,
  useHausingRooms,
  useHausingSettings,
  useUpdateHausingRoomMapping,
  useUpdateHausingSettings,
} from "@/api/rest/services/hausingService";
import { ApiBanner } from "@/components/common/Banner";
import Button from "@/components/common/Button";
import Checkbox from "@/components/common/Checkbox";
import Field from "@/components/common/Field";
import Input from "@/components/common/Input";
import Spinner from "@/components/common/Spinner";
import TextArea from "@/components/common/TextArea";

interface HausingSettingsProps {
  buildingId: number;
}

type HausingFormData = Omit<UpdateHausingSettingsRequest, "clientSecret">;

const toFormData = (settings: HausingSettingsData): HausingFormData => ({
  clientId: settings.clientId ?? "",
  companyId: settings.companyId ?? "",
  hausingBuildingId: settings.hausingBuildingId ?? "",
  ticketTitle: settings.ticketTitle ?? "",
  ticketDescription: settings.ticketDescription ?? "",
  syncDisabled: settings.syncDisabled,
  messageSyncDisabled: settings.messageSyncDisabled,
  exposeSubmitterEmail: settings.exposeSubmitterEmail,
  optOut: settings.optOut,
});

export function HausingSettings({ buildingId }: HausingSettingsProps) {
  const { t } = useTranslation();
  const { data: settings, isLoading, error } = useHausingSettings(buildingId);
  const { mutateAsync: saveSettings, isPending: isSaving } = useUpdateHausingSettings(buildingId);
  const { mutateAsync: saveRoomMapping } = useUpdateHausingRoomMapping(buildingId);
  const rooms = useHausingRooms(buildingId, Boolean(settings?.connectionConfigured));
  const [formData, setFormData] = useState<HausingFormData | null>(null);
  const [clientSecret, setClientSecret] = useState("");
  const [saveResult, setSaveResult] = useState<ApiResponse<unknown> | null>(null);

  useEffect(() => {
    if (!settings) {
      return;
    }

    setFormData(toFormData(settings));
    setClientSecret("");
  }, [settings]);

  const handleInputChange = useCallback(
    <FieldName extends keyof HausingFormData>(field: FieldName, value: HausingFormData[FieldName]) => {
      setFormData((current) => (current ? { ...current, [field]: value } : current));
    },
    [],
  );

  const handleSave = useCallback(async () => {
    if (!formData) {
      return;
    }

    setSaveResult(null);

    try {
      const savedSettings = await saveSettings({
        ...formData,
        ...(clientSecret.trim() ? { clientSecret: clientSecret.trim() } : {}),
      });
      setFormData(toFormData(savedSettings));
      setClientSecret("");
      setSaveResult({ success: true, result: null });
    } catch (saveError) {
      console.error("Failed to save Hausing settings:", saveError);
      setSaveResult({
        success: false,
        result: undefined,
        message: t("buildingSettings.hausing.saveError"),
      });
    }
  }, [clientSecret, formData, saveSettings, t]);

  if (error) {
    return <div className="py-4 text-red-600">{t("buildingSettings.hausing.loadError")}</div>;
  }

  if (isLoading || !formData || !settings) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
        {settings.connectionConfigured
          ? t("buildingSettings.hausing.connectionConfigured")
          : t("buildingSettings.hausing.connectionNotConfigured")}
      </div>

      <div className="grid grid-cols-1 gap-4">
        <Field id="hausing-client-id">
          <Field.Label>{t("building.hausingClientId")}</Field.Label>
          <Input
            type="text"
            value={formData.clientId}
            onChange={(event) => handleInputChange("clientId", event.target.value)}
            disabled={isSaving}
          />
        </Field>

        <Field id="hausing-client-secret">
          <Field.Label>{t("building.hausingClientSecret")}</Field.Label>
          <Input
            type="password"
            value={clientSecret}
            onChange={(event) => setClientSecret(event.target.value)}
            disabled={isSaving}
            autoComplete="new-password"
            aria-describedby="hausing-client-secret-help"
            placeholder={
              settings.clientSecretConfigured
                ? t("buildingSettings.hausing.clientSecretReplace")
                : t("buildingSettings.hausing.clientSecretRequired")
            }
          />
          <p id="hausing-client-secret-help" className="mt-1 text-xs text-gray-500">
            {settings.clientSecretConfigured
              ? t("buildingSettings.hausing.clientSecretStored")
              : t("buildingSettings.hausing.clientSecretNotStored")}
          </p>
        </Field>

        <Field id="hausing-company-id">
          <Field.Label>{t("building.hausingCompanyId")}</Field.Label>
          <Input
            type="text"
            value={formData.companyId}
            onChange={(event) => handleInputChange("companyId", event.target.value)}
            disabled={isSaving}
          />
        </Field>

        <Field id="hausing-building-id">
          <Field.Label>{t("building.hausingBuildingId")}</Field.Label>
          <Input
            type="text"
            value={formData.hausingBuildingId}
            onChange={(event) => handleInputChange("hausingBuildingId", event.target.value)}
            disabled={isSaving}
          />
        </Field>

        <Field id="hausing-ticket-title">
          <Field.Label>{t("building.hausingTicketTitle")}</Field.Label>
          <Input
            type="text"
            value={formData.ticketTitle}
            onChange={(event) => handleInputChange("ticketTitle", event.target.value)}
            disabled={isSaving}
          />
        </Field>

        <Field id="hausing-ticket-description">
          <Field.Label>{t("building.hausingTicketDescription")}</Field.Label>
          <TextArea
            value={formData.ticketDescription}
            onChange={(event) => handleInputChange("ticketDescription", event.target.value)}
            disabled={isSaving}
            rows={4}
          />
        </Field>

        <Checkbox
          id="hausing-sync-disabled"
          label={t("building.hausingSyncDisabled")}
          checked={formData.syncDisabled}
          onChange={(event) => handleInputChange("syncDisabled", event.target.checked)}
          disabled={isSaving}
        />

        <Checkbox
          id="hausing-message-sync-disabled"
          label={t("building.hausingMessageSyncDisabled")}
          checked={formData.messageSyncDisabled}
          onChange={(event) => handleInputChange("messageSyncDisabled", event.target.checked)}
          disabled={isSaving}
        />

        <Checkbox
          id="expose-submitter-email"
          label={t("building.exposeSubmitterEmailInHausing")}
          checked={formData.exposeSubmitterEmail}
          onChange={(event) => handleInputChange("exposeSubmitterEmail", event.target.checked)}
          disabled={isSaving}
        />

        <Checkbox
          id="hausing-opt-out"
          label={t("building.hausingOptOut")}
          checked={formData.optOut}
          onChange={(event) => handleInputChange("optOut", event.target.checked)}
          disabled={isSaving}
        />

        <div className="flex items-center gap-4">
          <Button type="button" onClick={handleSave} disabled={isSaving} loading={isSaving}>
            {isSaving ? t("settings.saving") : t("settings.save")}
          </Button>
          <div className="flex-grow">
            {saveResult && (
              <ApiBanner apiResponse={saveResult} successMessage={t("buildingSettings.hausing.saveSuccess")} />
            )}
          </div>
        </div>
      </div>

      <HausingRoomMappings
        settings={settings}
        rooms={rooms.data}
        isLoadingRooms={rooms.isLoading}
        roomsError={rooms.error}
        disabled={isSaving}
        onSave={async (householdId, roomId) => saveRoomMapping({ householdId, request: { roomId } })}
      />
    </div>
  );
}

interface HausingRoomMappingsProps {
  settings: HausingSettingsData;
  rooms: HausingRoom[] | undefined;
  isLoadingRooms: boolean;
  roomsError: Error | null;
  disabled: boolean;
  onSave: (householdId: number, roomId: string | null) => Promise<unknown>;
}

function HausingRoomMappings({
  settings,
  rooms,
  isLoadingRooms,
  roomsError,
  disabled,
  onSave,
}: HausingRoomMappingsProps) {
  const { t } = useTranslation();

  return (
    <section className="space-y-3 border-t border-gray-200 pt-6">
      <div>
        <h4 className="font-semibold text-gray-900">{t("buildingSettings.hausing.roomMappings")}</h4>
        <p className="mt-1 text-sm text-gray-600">{t("buildingSettings.hausing.roomMappingsHelp")}</p>
      </div>

      {!settings.connectionConfigured && (
        <p className="text-sm text-gray-600">{t("buildingSettings.hausing.roomMappingsUnavailable")}</p>
      )}

      {settings.connectionConfigured && isLoadingRooms && (
        <div className="flex items-center gap-2 py-3 text-sm text-gray-600">
          <Spinner className="h-4 w-4" />
          {t("buildingSettings.hausing.roomsLoading")}
        </div>
      )}

      {settings.connectionConfigured && roomsError && (
        <p className="text-sm text-red-600">{t("buildingSettings.hausing.roomsLoadError")}</p>
      )}

      {settings.connectionConfigured && rooms && settings.roomMappings.length === 0 && (
        <p className="text-sm text-gray-600">{t("buildingSettings.hausing.noFlats")}</p>
      )}

      {settings.connectionConfigured && rooms && settings.roomMappings.length > 0 && (
        <div className="space-y-3">
          {settings.roomMappings.map((mapping) => (
            <HausingRoomMappingRow
              key={mapping.householdId}
              mapping={mapping}
              rooms={rooms}
              disabled={disabled}
              onSave={onSave}
            />
          ))}
        </div>
      )}
    </section>
  );
}

interface HausingRoomMappingRowProps {
  mapping: HausingRoomMapping;
  rooms: HausingRoom[];
  disabled: boolean;
  onSave: (householdId: number, roomId: string | null) => Promise<unknown>;
}

function HausingRoomMappingRow({ mapping, rooms, disabled, onSave }: HausingRoomMappingRowProps) {
  const { t } = useTranslation();
  const initialRoomId = mapping.roomId ?? "";
  const [selectedRoomId, setSelectedRoomId] = useState(initialRoomId);
  const [savedRoomId, setSavedRoomId] = useState(initialRoomId);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setSelectedRoomId(initialRoomId);
    setSavedRoomId(initialRoomId);
  }, [initialRoomId]);

  const isDirty = selectedRoomId !== savedRoomId;
  const hasUnknownCurrentRoom = Boolean(initialRoomId) && !rooms.some((room) => room.id === initialRoomId);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setSaveError(null);

    try {
      await onSave(mapping.householdId, selectedRoomId || null);
      setSavedRoomId(selectedRoomId);
    } catch (error) {
      console.error("Failed to save Hausing room mapping:", error);
      setSaveError(t("buildingSettings.hausing.roomSaveError"));
    } finally {
      setIsSaving(false);
    }
  }, [mapping.householdId, onSave, selectedRoomId, t]);

  return (
    <div className="rounded-md border border-gray-200 bg-white p-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="min-w-0 flex-1">
          <label htmlFor={`hausing-room-${mapping.householdId}`} className="block text-sm font-medium text-gray-700">
            {mapping.householdTitle}
          </label>
          <select
            id={`hausing-room-${mapping.householdId}`}
            value={selectedRoomId}
            onChange={(event) => setSelectedRoomId(event.target.value)}
            disabled={disabled || isSaving}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-dobu-orange focus:ring-dobu-orange sm:text-sm"
          >
            <option value="">{t("buildingSettings.hausing.noRoom")}</option>
            {hasUnknownCurrentRoom && <option value={initialRoomId}>{initialRoomId}</option>}
            {rooms.map((room) => (
              <option key={room.id} value={room.id}>
                {room.name} ({room.id})
              </option>
            ))}
          </select>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleSave}
          disabled={!isDirty || disabled || isSaving}
          loading={isSaving}
        >
          {t("settings.save")}
        </Button>
      </div>
      {saveError && <p className="mt-2 text-sm text-red-600">{saveError}</p>}
    </div>
  );
}
